import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Button, Card, Cluster, EmptyState, Input, Stack } from "@salesapp/ui";
import { crmApi, type SearchHit } from "../../lib/crmApi";
import { opsApi, type AppNotification } from "../../lib/opsApi";
import { useCrmHref } from "../../lib/customerUrl";
import { useLocale } from "../../shell/LocaleProvider";

const RECORD_PATHS: Record<string, (id: string) => string> = {
  company: (id) => `/companies/${id}`,
  lead: (id) => `/leads/${id}`,
  opportunity: (id) => `/opportunities/${id}`,
  quote: (id) => `/quotes/${id}`,
  order: (id) => `/orders/${id}`,
  invoice: (id) => `/invoices/${id}`,
  support: (id) => `/support/${id}`,
};

function hitPath(hit: SearchHit): string | null {
  if (hit.record_type === "contact") {
    if (!hit.company_id) return null;
    return `/companies/${hit.company_id}?contact=${encodeURIComponent(hit.record_id)}`;
  }
  return RECORD_PATHS[hit.record_type]?.(hit.record_id) ?? null;
}

export function SearchPage({ orgId }: { orgId: string }) {
  const crmHref = useCrmHref();
  const [params, setParams] = useSearchParams();
  const query = params.get("q") ?? "";
  const [items, setItems] = useState<SearchHit[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  function setQuery(next: string) {
    if (next.trim()) setParams({ q: next }, { replace: true });
    else setParams({}, { replace: true });
  }

  useEffect(() => {
    const needle = query.trim();
    if (needle.length < 2) {
      setItems([]);
      setSearched(false);
      return;
    }
    const handle = window.setTimeout(() => {
      void crmApi.search(orgId, needle, 25).then((result) => {
        setItems(result.items);
        setSearched(true);
        setError(null);
      }).catch((err) => {
        setError(err instanceof Error ? err.message : "Search could not be completed.");
      });
    }, 250);
    return () => window.clearTimeout(handle);
  }, [orgId, query]);

  return (
    <Stack gap={4}>
      <h1 className="home-title">Search</h1>
      <Input
        label="Search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Company, person, lead, deal, quote, order, invoice, or ticket"
      />
      {error ? <p className="crm-error" role="alert">{error}</p> : null}
      {searched && items.length === 0 ? (
        <EmptyState title="No matches" description="Nothing in this organisation matches that search." />
      ) : null}
      {items.map((hit) => {
        const path = hitPath(hit);
        return (
          <Card key={`${hit.record_type}-${hit.record_id}`}>
            <Stack gap={1}>
              {path ? (
                <Link to={crmHref(path)}><strong>{hit.title}</strong></Link>
              ) : (
                <strong>{hit.title}</strong>
              )}
              <p className="crm-muted">{hit.subtitle}</p>
            </Stack>
          </Card>
        );
      })}
    </Stack>
  );
}

export function NotificationsPage({ orgId }: { orgId: string }) {
  const { formatDateTime } = useLocale();
  const [items, setItems] = useState<AppNotification[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    void opsApi.listNotifications(orgId).then((result) => {
      setItems(result.notifications);
    }).catch((err) => {
      setError(err instanceof Error ? err.message : "Notifications could not be loaded.");
    });
  }, [orgId]);

  function dismiss(id: string) {
    void opsApi.dismissNotification(orgId, id).then(() => {
      setItems((current) => current.filter((item) => item.id !== id));
    }).catch((err) => {
      setError(err instanceof Error ? err.message : "That notification could not be dismissed.");
    });
  }

  function dismissAll() {
    setClearing(true);
    void opsApi.dismissAllNotifications(orgId).then(() => {
      setItems([]);
    }).catch((err) => {
      setError(err instanceof Error ? err.message : "Notifications could not be cleared.");
    }).finally(() => setClearing(false));
  }

  return (
    <Stack gap={4}>
      <Cluster gap={3} align="center" justify="between">
        <h1 className="home-title">Notifications</h1>
        {items.length > 0 ? (
          <Button type="button" size="sm" variant="secondary" disabled={clearing} onClick={dismissAll}>
            Dismiss all
          </Button>
        ) : null}
      </Cluster>
      {error ? <p className="crm-error" role="alert">{error}</p> : null}
      {items.length === 0 ? (
        <EmptyState title="No notifications" description="Nothing is waiting on you right now." />
      ) : null}
      {items.map((item) => (
        <Card key={item.id}>
          <Stack gap={2}>
            <div className="notification-card__head">
              <strong>{item.title}</strong>
              {item.created_at ? (
                <time className="notification-card__when" dateTime={item.created_at}>
                  {formatDateTime(item.created_at)}
                </time>
              ) : null}
            </div>
            <p className="crm-muted">{item.body}</p>
            <Cluster gap={2}>
              {item.href ? <Link to={item.href}>Open</Link> : null}
              <Button type="button" size="sm" variant="secondary" onClick={() => dismiss(item.id)}>Dismiss</Button>
            </Cluster>
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}
